import React, { Component } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import Routes from 'constants/routes';
import VideoModal from 'components/VideoModal';
import Footer from 'components/Footer';

import { getClientInfo } from 'utils/get-client-info';
import { resetModuleData } from 'store/actions/module';
import LoginRedirect from '@lwtears/lwt-common-frontend/lib/Login/LoginRedirect';

import withRouter from '../../hooks/withRouter';
import { toBoolean } from '../../utils/string-util';

import 'styles/global.scss';

// TODO : remove requesting mic permissions
import { getMicPermissions } from 'utils/mic-permissions';
import {
  setMobile,
  setMicPermissions,
  setLiteVersion,
  setAccessibilityMode
} from 'store/actions/ui';

import { setActivity, toggleVideo, toggleModal } from 'store/actions/activity';

import { applyA11ySettings } from '@lwtears/lwt-common-frontend/lib/@common/util/a11y-util';
import { setLocale } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { getAudioVendor } from 'utils/accessibilityUtils';
import { setSessionId } from '@lwtears/lwt-common-frontend/lib/@common';
import { withLwtStore } from '@lwtears/lwt-common-frontend/lib/LwtApp';
import { parseQueryParams } from '@lwtears/lwt-common-frontend/lib/@common/util/query-params-util';

const initAnalytics = () => {
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }

  gtag('js', new Date());
  gtag('config', process.env.GOOGLE_KEY);
};

const Version = () => {
  const { pathname } = useLocation();

  return pathname.includes('/app-selection') ? (
    <div
      style={{
        backgroundColor: '#e9f6cc',
        textAlign: 'right',
        paddingBottom: 4,
        paddingRight: 8,
        fontSize: 10
      }}
    >
      Version: {process.env.REACT_APP_APP_VERSION}
    </div>
  ) : (
    <></>
  );
};
export class App extends Component {
  constructor() {
    super();

    this.state = {
      init: false
    };

    this.logoutTimeout = null;
    this.logoutTimeoutDuration = (parseInt(process.env.REACT_APP_LOGOUT_TIMER) || 60) * 60 * 1000;
  }

  handleInactivity = () => {
    setSessionId('');
    LoginRedirect();
  };

  keepFocusInsideModal = event => {
    const focusableElements = 'button, input';
    const modal = document.querySelector('#tutorial-modal');
    const firstFocusableElement = modal.querySelectorAll(focusableElements)[0];
    const focusableContent = modal.querySelectorAll(focusableElements);
    const lastFocusableElement = focusableContent[focusableContent.length - 1].pressed
      ? focusableContent[focusableContent.length - 2]
      : focusableContent[focusableContent.length - 1];
    if (
      document.activeElement === lastFocusableElement ||
      document.activeElement === document.body
    ) {
      firstFocusableElement.focus();
      event.preventDefault();
    }
  };

  initEventListeners = () => {
    const classList = document.body.classList;
    document.addEventListener('mousedown', () => {
      if (classList.contains('using-keyboard')) classList.remove('using-keyboard');
    });
    document.addEventListener('keydown', event => {
      if (event.key === 'Tab') {
        !classList.contains('using-keyboard') && classList.add('using-keyboard');
        if (this.props.isVideoModalOpen) {
          this.keepFocusInsideModal(event);
        }
      }
      if (event.key === 'Escape' && this.props.isVideoModalOpen) {
        this.props.toggleVideo(false);
      }
    });
  };

  async componentDidMount() {
    this.initEventListeners();
    if (getClientInfo().platform.type === 'tablet') {
      this.props.setMobile(true);
    }

    initAnalytics();

    // TODO : microphone permissions, remove until resolved
    const {
      props: { setMicPermissions }
    } = this;

    if (typeof navigator.mediaDevices !== 'undefined') {
      getMicPermissions()
        .then(function(stream) {
          setMicPermissions(true);
          // Close stream immediately
          stream.getAudioTracks().forEach(track => track.stop());
        })
        .catch(function() {
          setMicPermissions(false);
        });
    } else {
      setMicPermissions(false);
    }

    applyA11ySettings(this.props.lwtStoreState.settings, getAudioVendor());

    const {
      props: { router, setLiteVersion, setAccessibilityMode, setActivity }
    } = this;

    if (router?.location?.search) {
      const { lite, locale, accessibilityMode, ...restParams } = parseQueryParams(
        router?.location.search
      );

      if (lite) {
        setLiteVersion(true);
        setAccessibilityMode(toBoolean(accessibilityMode));
        if (locale) setLocale(locale);
        await setActivity(restParams);
      }
    }

    this.setState({ init: true });
    window.addEventListener('keydown', this.handleKeyDown);
  }

  componentDidUpdate(prevProps) {
    const { activityTimestamp: prevActivityTimestamp } = prevProps;
    const {
      props: { activityTimestamp, isLiteVersion },
      logoutTimeoutDuration
    } = this;

    if (isLiteVersion) return;

    if (activityTimestamp && prevActivityTimestamp !== activityTimestamp) {
      // eslint-disable-next-line no-console
      console.log(
        `setting new logout timer, you have ${logoutTimeoutDuration /
          1000 /
          60} minutes. use them wisely!`
      );
      this.logoutTimeout && clearTimeout(this.logoutTimeout);
      this.logoutTimeout = setTimeout(() => this.handleInactivity(), logoutTimeoutDuration);
    }
  }

  componentWillUnmount() {
    this.logoutTimeout && clearTimeout(this.logoutTimeout);
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  render() {
    const {
      state: { init }
    } = this;

    if (!init) {
      return (
        <div className={'componentWrapper'}>
          <div className={['loading', 'loader'].join(' ')}></div>
        </div>
      );
    }
    return (
      <div id="mainContainer" role="main">
        {this.props.isVideoModalOpen && <VideoModal />}
        {process.env.IS_TEST_ENVIRONMENT && !this.props.isLiteVersion && (
          <div className="test-links">
            <Link to="/tester">Tester</Link>
          </div>
        )}

        <Routes />
        <Footer />
        <Version />
      </div>
    );
  }
}

App.propTypes = {
  loggedIn: PropTypes.bool,
  setMobile: PropTypes.func.isRequired,
  resetModuleData: PropTypes.func.isRequired,
  setMicPermissions: PropTypes.func,
  router: PropTypes.shape({
    navigate: PropTypes.func,
    location: PropTypes.object
  }),
  activityTimestamp: PropTypes.number,
  toggleVideo: PropTypes.func,
  toggleModal: PropTypes.func,
  isVideoModalOpen: PropTypes.bool,
  lwtStoreState: PropTypes.object,
  setLiteVersion: PropTypes.func,
  setAccessibilityMode: PropTypes.func,
  setActivity: PropTypes.func,
  isLiteVersion: PropTypes.bool
};

export default connect(
  state => ({
    isVideoModalOpen: state.activity.isVideoModalOpen,
    loggedIn: state.login.loggedIn,
    activityTimestamp: state.ui.activityTimestamp,
    isLiteVersion: state.ui.isLiteVersion
  }),
  {
    resetModuleData,
    setMicPermissions,
    setMobile,
    toggleVideo,
    toggleModal,
    setLiteVersion,
    setAccessibilityMode,
    setActivity
  }
)(withLwtStore(withRouter(App)));
